import { FormEvent, useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { getDepartments, updateMyStaffProfile } from '../lib/api';
import { errorMessage, normalizeProfileText } from '../lib/utils';
import type { Department } from '../types';
import { useStaffAuth } from '../auth/StaffAuthContext';
import { useToast } from '../context/ToastContext';

export default function ProfilePage() {
  const { identity, refreshProfile } = useStaffAuth();
  const { showToast } = useToast();
  const [name, setName] = useState(identity.full_name ?? '');
  const [departmentId, setDepartmentId] = useState(identity.department_id?.toString() ?? '');
  const [departments, setDepartments] = useState<Department[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!identity.isAuthenticated) return;
    getDepartments().then(setDepartments).catch((error) => showToast(errorMessage(error), 'error'));
  }, [identity.isAuthenticated, showToast]);

  if (!identity.email) return null;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const selectedDepartmentId = Number(departmentId);
    if (name.trim().length < 2 || !Number.isInteger(selectedDepartmentId) || selectedDepartmentId <= 0) {
      showToast('Lengkapkan nama dan bahagian anda.', 'error');
      return;
    }

    setBusy(true);
    try {
      await updateMyStaffProfile({
        fullName: normalizeProfileText(name),
        departmentId: selectedDepartmentId,
      });
      await refreshProfile();
      showToast('Profil dikemaskini. Rekod kehadiran terdahulu tidak berubah.', 'success');
    } catch (error) {
      showToast(errorMessage(error), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout title="Profil Saya">
      <section className="section-block profile-page-card">
        <div className="profile-page-intro">
          <div>
            <div className="eyebrow">Profil akaun</div>
            <h2>Kemaskini maklumat semasa</h2>
            <p className="muted">Perubahan hanya digunakan untuk <em>check-in</em> akan datang. Rekod terdahulu mengekalkan nama dan bahagian/organisasi semasa <em>check-in</em>.</p>
          </div>
        </div>

        <form className="form-stack profile-form" onSubmit={submit}>
          <label className="field">
            <span>Alamat e-mel</span>
            <input type="email" value={identity.email} readOnly aria-readonly="true" />
            <small className="field-help">E-mel ialah identiti profil dan tidak boleh diubah di sini.</small>
          </label>

          <label className="field">
            <span>Nama penuh</span>
            <input value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} autoComplete="name" />
          </label>

          <label className="field">
            <span>Bahagian</span>
            <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)} required>
              <option value="">Pilih bahagian</option>
              {departments.map((department) => (
                <option key={department.department_id} value={department.department_id}>{department.department_name}</option>
              ))}
            </select>
          </label>

          <div className="form-actions profile-form-actions">
            <button className="button button-primary" disabled={busy}>{busy ? 'Sedang menyimpan…' : 'Simpan profil'}</button>
          </div>
        </form>
      </section>
    </Layout>
  );
}
